/** Resolve image URL from food master row metadata */
import { resolveAssetUrl } from './assetUrl';

export type FoodMasterRow = {
  code: string;
  name: string;
  metadata?: Record<string, unknown>;
};

/** Square 400×400 thumbnails (1:1, within 1200×1200 max). */
function squareImage(photoId: string): string {
  return `https://images.unsplash.com/${photoId}?w=400&h=400&fit=crop&auto=format`;
}

/** Default category images — used when stored URLs are missing or known-broken. */
export const FOOD_CATEGORY_DEFAULT_IMAGES: Record<string, string> = {
  pizza: squareImage('photo-1513104890138-7c749659a591'),
  burger: squareImage('photo-1568901346375-23c9450c58cd'),
  biryani: squareImage('photo-1596797038530-2c107229654b'),
  chinese: squareImage('photo-1526318472351-cadfcf864360'),
  beverages: squareImage('photo-1544145945-f90425340c7e'),
  desserts: squareImage('photo-1551024506-0bccd828d307'),
  south_indian_meals: squareImage('photo-1585937421612-70a008356fbe'),
};

/** Default cuisine images — used when stored URLs are missing or known-broken. */
export const CUISINE_DEFAULT_IMAGES: Record<string, string> = {
  south_indian: squareImage('photo-1585937421612-70a008356fbe'),
  north_indian: squareImage('photo-1565557623262-b51c2513a641'),
  chinese: squareImage('photo-1526318472351-cadfcf864360'),
  italian: squareImage('photo-1574071318508-1cdbab80d002'),
  arabian: squareImage('photo-1544025162-d76694265947'),
  mexican: squareImage('photo-1565299585323-38174c4aab1e'),
  american: squareImage('photo-1568901346375-23c9450c58cd'),
};

/** Legacy Unsplash IDs that 404 — auto-replace for existing tenant rows. */
const BROKEN_FOOD_CATEGORY_IMAGE_IDS = [
  'photo-1563379091339-03246963d96a',
  'photo-1525755662778-989d57609567',
  'photo-1633945274405-573c06427067',
];

export function resolveFoodCategorySampleImageUrl(categoryCode: string): string | undefined {
  const code = categoryCode.trim().toLowerCase();
  if (!code) return undefined;
  return FOOD_CATEGORY_DEFAULT_IMAGES[code];
}

export function resolveCuisineSampleImageUrl(cuisineCode: string): string | undefined {
  const code = cuisineCode.trim().toLowerCase();
  if (!code) return undefined;
  return CUISINE_DEFAULT_IMAGES[code];
}

export function isBrokenFoodCategoryImageUrl(url: string): boolean {
  const value = url.trim();
  if (!value) return false;
  return BROKEN_FOOD_CATEGORY_IMAGE_IDS.some((id) => value.includes(id));
}

export function resolveFoodCategoryListImageUrl(
  code: string,
  metadata?: Record<string, unknown>,
): { primary: string; fallback: string } {
  return resolveMasterListImageUrl(code, metadata, FOOD_CATEGORY_DEFAULT_IMAGES);
}

export function resolveCuisineListImageUrl(
  code: string,
  metadata?: Record<string, unknown>,
): { primary: string; fallback: string } {
  return resolveMasterListImageUrl(code, metadata, CUISINE_DEFAULT_IMAGES);
}

/** Default addon images — used when stored URLs are missing or known-broken. */
export const FOOD_ADDON_DEFAULT_IMAGES: Record<string, string> = {
  extra_cheese: squareImage('photo-1486297678162-eb2a8836-f9b8'),
  extra_chicken: squareImage('photo-1608038536079-82a169a8d3da'),
  mushroom: squareImage('photo-1518977956815-c8310f4ab9f4'),
  jalapeno: squareImage('photo-1608897012878-4c0d12917449'),
  mozzarella: squareImage('photo-1486297678162-eb2a8836-f9b8'),
  coke: squareImage('photo-1544145945-f90425340c7e'),
  french_fries: squareImage('photo-1573080496219-b998aa562a88'),
};

export const GENERIC_FOOD_ADDON_IMAGE = squareImage('photo-1486297678162-eb2a8836-f9b8');

/** Offline-safe placeholder — always loads when remote defaults fail. */
export const FOOD_ADDON_PLACEHOLDER_DATA_URI = (
  'data:image/svg+xml,'
  + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96">'
    + '<rect width="96" height="96" rx="12" fill="#f1f5f9"/>'
    + '<circle cx="48" cy="40" r="18" fill="#cbd5e1"/>'
    + '<path d="M24 72c4-12 14-18 24-18s20 6 24 18" fill="#94a3b8"/>'
    + '</svg>',
  )
);

const BROKEN_FOOD_ADDON_IMAGE_IDS = [
  'photo-1606755450119-8e4a5a4e4c4e',
];

/** Default food-item images keyed by menu foodCode — used when stored URLs are missing or broken. */
export const FOOD_ITEM_DEFAULT_IMAGES: Record<string, string> = {
  margherita: squareImage('photo-1574071318508-1cdbab80d002'),
  chicken_biryani: squareImage('photo-1596797038530-2c107229654b'),
  mutton_biryani: squareImage('photo-1596797038530-2c107229654b'),
  masala_dosa: squareImage('photo-1585937421612-70a008356fbe'),
  idli_sambar: squareImage('photo-1585937421612-70a008356fbe'),
  filter_coffee: squareImage('photo-1495474472287-4d71bcdd2085'),
};

export const GENERIC_FOOD_ITEM_IMAGE = squareImage('photo-1585937421612-70a008356fbe');

/** Legacy Unsplash IDs that 404 for seeded food image masters. */
const BROKEN_FOOD_ITEM_IMAGE_IDS = [
  'photo-1563379091339-03246963d96a',
  'photo-1630384060420-cdcfd1f2f2a3',
  'photo-1589301760014-d929f3979dbb',
];

export function isBrokenFoodAddonImageUrl(url: string): boolean {
  const value = url.trim();
  if (!value) return false;
  return BROKEN_FOOD_ADDON_IMAGE_IDS.some((id) => value.includes(id))
    || isBrokenFoodCategoryImageUrl(value);
}

export function isBrokenFoodItemImageUrl(url: string): boolean {
  const value = url.trim();
  if (!value) return false;
  return BROKEN_FOOD_ITEM_IMAGE_IDS.some((id) => value.includes(id))
    || isBrokenFoodCategoryImageUrl(value)
    || isBrokenFoodAddonImageUrl(value);
}

export function resolveFoodItemSampleImageUrl(foodCode: string): string | undefined {
  const code = foodCode.trim().toLowerCase();
  if (!code) return GENERIC_FOOD_ITEM_IMAGE;
  return FOOD_ITEM_DEFAULT_IMAGES[code] || GENERIC_FOOD_ITEM_IMAGE;
}

export function resolveFoodImageListImageUrl(
  metadata?: Record<string, unknown>,
): { primary: string; fallback: string } {
  const foodCode = String(metadata?.foodCode ?? '').trim().toLowerCase();
  return resolveFoodItemMasterListImageUrl(foodCode, metadata);
}

function resolveFoodItemMasterListImageUrl(
  foodCode: string,
  metadata: Record<string, unknown> | undefined,
): { primary: string; fallback: string } {
  const raw = String(metadata?.imageUrl ?? metadata?.icon ?? metadata?.image ?? '').trim();
  const defaultRaw = FOOD_ITEM_DEFAULT_IMAGES[foodCode] || GENERIC_FOOD_ITEM_IMAGE;
  const defaultResolved = defaultRaw ? resolveAssetUrl(defaultRaw) : '';
  const genericResolved = resolveAssetUrl(GENERIC_FOOD_ITEM_IMAGE);

  if (raw && !isBrokenFoodItemImageUrl(raw)) {
    return {
      primary: resolveAssetUrl(raw),
      fallback: defaultResolved && defaultResolved !== genericResolved ? defaultResolved : genericResolved,
    };
  }
  if (defaultResolved) {
    return {
      primary: defaultResolved,
      fallback: genericResolved && genericResolved !== defaultResolved ? genericResolved : '',
    };
  }
  return { primary: raw ? resolveAssetUrl(raw) : '', fallback: '' };
}

/** Ordered image sources for food image master list cells. */
export function resolveFoodImageSources(
  metadata?: Record<string, unknown>,
): string[] {
  const { primary, fallback } = resolveFoodImageListImageUrl(metadata);
  const generic = resolveAssetUrl(GENERIC_FOOD_ITEM_IMAGE);
  const chain = [primary, fallback, generic, FOOD_ADDON_PLACEHOLDER_DATA_URI];
  return chain.filter((src, index) => src && chain.indexOf(src) === index);
}

export function resolveFoodAddonSampleImageUrl(addonCode: string): string | undefined {
  const code = addonCode.trim().toLowerCase();
  if (!code) return GENERIC_FOOD_ADDON_IMAGE;
  return FOOD_ADDON_DEFAULT_IMAGES[code] || GENERIC_FOOD_ADDON_IMAGE;
}

export function resolveFoodAddonListImageUrl(
  code: string,
  metadata?: Record<string, unknown>,
): { primary: string; fallback: string } {
  return resolveMasterListImageUrl(code, metadata, FOOD_ADDON_DEFAULT_IMAGES, GENERIC_FOOD_ADDON_IMAGE);
}

/** Ordered image sources for list cells — uploaded URL, per-addon default, generic, offline placeholder. */
export function resolveFoodAddonImageSources(
  code: string,
  metadata?: Record<string, unknown>,
): string[] {
  const { primary, fallback } = resolveFoodAddonListImageUrl(code, metadata);
  const generic = resolveAssetUrl(GENERIC_FOOD_ADDON_IMAGE);
  const chain = [primary, fallback, generic, FOOD_ADDON_PLACEHOLDER_DATA_URI];
  return chain.filter((src, index) => src && chain.indexOf(src) === index);
}

export function foodAddonHasCustomUpload(metadata?: Record<string, unknown>): boolean {
  const raw = String(metadata?.imageUrl ?? metadata?.icon ?? metadata?.image ?? '').trim();
  return !!raw && !isBrokenFoodCategoryImageUrl(raw) && !isBrokenFoodAddonImageUrl(raw);
}

export function resolveFoodAddonImagePreviewUrl(
  addonCode: string,
  metadata?: Record<string, unknown>,
): string {
  const raw = String(metadata?.imageUrl ?? '').trim();
  if (raw && !isBrokenFoodAddonImageUrl(raw)) {
    return resolveAssetUrl(raw);
  }
  const sample = resolveFoodAddonSampleImageUrl(addonCode);
  return sample ? resolveAssetUrl(sample) : '';
}

function resolveMasterListImageUrl(
  code: string,
  metadata: Record<string, unknown> | undefined,
  defaults: Record<string, string>,
  genericFallback?: string,
): { primary: string; fallback: string } {
  const raw = String(metadata?.imageUrl ?? metadata?.icon ?? metadata?.image ?? '').trim();
  const defaultRaw = defaults[code.trim().toLowerCase()] || genericFallback || '';
  const genericResolved = genericFallback ? resolveAssetUrl(genericFallback) : '';
  const defaultResolved = defaultRaw ? resolveAssetUrl(defaultRaw) : '';
  const secondaryFallback = defaultResolved && defaultResolved !== genericResolved
    ? defaultResolved
    : genericResolved;

  if (raw && !isBrokenFoodCategoryImageUrl(raw) && !isBrokenFoodAddonImageUrl(raw) && !isBrokenFoodItemImageUrl(raw)) {
    return {
      primary: resolveAssetUrl(raw),
      fallback: secondaryFallback,
    };
  }
  if (defaultResolved) {
    return {
      primary: defaultResolved,
      fallback: genericResolved && genericResolved !== defaultResolved ? genericResolved : '',
    };
  }
  if (genericResolved) {
    return { primary: genericResolved, fallback: '' };
  }
  return { primary: raw ? resolveAssetUrl(raw) : '', fallback: '' };
}

export function masterImageUrl(row?: FoodMasterRow | null): string {
  if (!row?.metadata) return '';
  return String(row.metadata.imageUrl || row.metadata.logoUrl || '');
}

export function findFoodImageFromMasters(
  foodImages: FoodMasterRow[],
  foodCode: string,
  imageType: 'thumbnail' | 'gallery' = 'thumbnail',
): string {
  const match = foodImages.find((row) => {
    const meta = row.metadata || {};
    return String(meta.foodCode || '') === foodCode && String(meta.imageType || 'thumbnail') === imageType;
  });
  if (!match) return '';
  const { primary } = resolveFoodImageListImageUrl(match.metadata);
  return primary || masterImageUrl(match);
}

export function resolveFoodThumbnailFromMasters(
  opts: {
    foodCode?: string;
    categoryCode?: string;
    cuisineCode?: string;
    foodImages?: FoodMasterRow[];
    categories?: FoodMasterRow[];
    cuisines?: FoodMasterRow[];
  },
): string {
  const { foodCode, categoryCode, cuisineCode, foodImages = [], categories = [], cuisines = [] } = opts;
  if (foodCode) {
    const fromFood = findFoodImageFromMasters(foodImages, foodCode, 'thumbnail');
    if (fromFood) return fromFood;
  }
  if (categoryCode) {
    const cat = categories.find((c) => c.code === categoryCode);
    const url = masterImageUrl(cat);
    if (url) return url;
  }
  if (cuisineCode) {
    const cuisine = cuisines.find((c) => c.code === cuisineCode);
    return masterImageUrl(cuisine);
  }
  return '';
}

export function resolveRestaurantImagesFromMasters(
  cuisineCodes: string[],
  restaurantTypeCode: string,
  cuisines: FoodMasterRow[],
  restaurantTypes: FoodMasterRow[],
): { logo: string; coverImage: string } {
  const primaryCuisine = cuisines.find((c) => cuisineCodes.includes(c.code));
  const typeRow = restaurantTypes.find((t) => t.code === restaurantTypeCode);
  const cuisineImg = masterImageUrl(primaryCuisine);
  const typeImg = masterImageUrl(typeRow);
  return {
    logo: cuisineImg || typeImg,
    coverImage: typeImg || cuisineImg,
  };
}

export type OpeningHourRow = { day: string; openTime: string; closeTime: string; isClosed?: boolean };

export const WEEK_DAYS = [
  { value: 'mon', label: 'Mon' }, { value: 'tue', label: 'Tue' }, { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' }, { value: 'fri', label: 'Fri' }, { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
];

export function defaultOpeningHours(): OpeningHourRow[] {
  return WEEK_DAYS.map((d) => ({ day: d.value, openTime: '10:00', closeTime: '22:00', isClosed: false }));
}

export function openingHoursFromMasters(templates: FoodMasterRow[]): OpeningHourRow[] {
  const byDay: Record<string, OpeningHourRow> = {};
  for (const row of templates) {
    const meta = row.metadata || {};
    const day = String(meta.day || '');
    if (!day) continue;
    byDay[day] = {
      day,
      openTime: String(meta.openTime || '10:00'),
      closeTime: String(meta.closeTime || '22:00'),
      isClosed: Boolean(meta.isClosed),
    };
  }
  return WEEK_DAYS.map((d) => byDay[d.value] || { day: d.value, openTime: '10:00', closeTime: '22:00', isClosed: false });
}
